import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiTrash2 } from "react-icons/fi"; // Fi icons
import { FaArrowDown, FaArrowUp } from "react-icons/fa"; // Fa icons
import PDFUploader from "./PDFUploader";
import { capitalizeWords } from "../../ui/capitalizeWords";
import ImageModal from "../Items Master/ImageModal";
import Select from "react-select";
import { HiOutlineX } from "react-icons/hi";

const style = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "30px",
    height: "30px",
    borderRadius: "6px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "30px",
    padding: "0 4px",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "30px",
  }),
};

const ForClientOrderModal = ({ isOpen, closeModal }) => {
  const [items, setItems] = useState([
    {
      categoryName: "",
      itemName: "",
      pcsPerCtn: "",
      qty: 1,
      price: "",
      quantitycarton: 0,
      wareHouseName: "",
      inwardReference: "",
      inwardShipmentMark: "",
    },
  ]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryItems, setSelectedCategoryItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [transporterName, setTransporterName] = useState("");
  const [transporters, setTransporters] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // Scroll Refs
  const formRef = useRef(null);
  const topRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control, // Added control for Controller
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { gst: 0 },
  });

  const gst = watch("gst", 0);
  const token = localStorage.getItem("token");

  // Scroll Behavior
  useEffect(() => {
    if (!formRef.current || !topRef.current) return;
    const observerDown = new IntersectionObserver(
      ([entry]) => setShowScrollDown(!entry.isIntersecting),
      { threshold: 0 }
    );
    const observerUp = new IntersectionObserver(
      ([entry]) => setShowScrollUp(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-10px" }
    );
    observerDown.observe(formRef.current);
    observerUp.observe(topRef.current);
    return () => {
      observerDown.disconnect();
      observerUp.disconnect();
    };
  }, [formRef, topRef]);

  // Fetch data
  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const response = await axios.get(
          buildApiUrl("/api/warehouse/inventory/warehouse-details"),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWarehouses(response.data.data);
      } catch (error) {
        console.error("Error fetching warehouse data:", error.message);
      }
    };
    const fetchTransporterData = async () => {
      try {
        const response = await axios.get(
          buildApiUrl("/api/transporter"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTransporters(response.data);
      } catch (error) {
        console.error("Error fetching transporter data:", error.message);
      }
    };
    fetchTransporterData();
    fetchWarehouseData();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientResponse = await axios.get(
          buildApiUrl("/api/clients"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClients(clientResponse.data);
        const categoryResponse = await axios.get(
          buildApiUrl("/api/categories/all-categories"),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories(categoryResponse.data.categories);
        const allItemsList = categoryResponse.data.categories.flatMap(
          (category) => category.items
        );
        setAllItems(allItemsList);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [token]);

  // Handlers
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const orderData = {
        clientId: selectedClient.user,
        items,
        gst: data.gst,
        transporterName,
      };
      const pdfUploader = PDFUploader({ orderData, token, selectedClient });
      const pdfUrl = await pdfUploader.generatePDFAndUpload();
      orderData.pdfUrl = pdfUrl;
      await axios.post(
        buildApiUrl("/api/orderItem/create-on-behalf"),
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order created successfully!");
      closeModal();
      window.location.reload();
    } catch (error) {
      console.error("Error creating order:", error.message);
      toast.error("Error creating order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        categoryName: "",
        itemName: "",
        qty: 1,
        price: 0,
        pcsPerCtn: 0,
        quantitycarton: 0,
        wareHouseName: "",
        inwardReference: "",
        inwardShipmentMark: "",
      },
    ]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.error("At least one item is required.");
      return;
    }
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  const handleItemChange = (index, event) => {
    const updatedItems = [...items];
    const { name, value } = event.target;
    updatedItems[index][name] = value;
    if (name === "qty") {
      const qty = parseInt(value) || 0;
      const pcsPerCtn = parseInt(updatedItems[index].pcsPerCtn) || 0;
      updatedItems[index].quantitycarton =
        pcsPerCtn > 0 ? Math.floor(qty / pcsPerCtn) : 0;
    }
    if (name === "quantitycarton") {
      const qtyCarton = parseInt(value) || 0;
      const pcsPerCtn = parseInt(updatedItems[index].pcsPerCtn) || 0;
      updatedItems[index].qty = pcsPerCtn > 0 ? qtyCarton * pcsPerCtn : 0;
    }
    setItems(updatedItems);
  };

  const handlePcsPerCtnChange = (index, event) => {
    const updatedItems = [...items];
    const pcsPerCtn = parseInt(event.target.value) || 0;
    updatedItems[index].pcsPerCtn = pcsPerCtn;
    if (pcsPerCtn === 0) {
      updatedItems[index].quantitycarton = 0;
      updatedItems[index].qty = 0;
    }
    setItems(updatedItems);
  };

  const handleCategoryChange = (index, event) => {
    const selectedCategory = event.target.value;
    const category = categories.find(
      (cat) => cat.categoryName === selectedCategory
    );
    const itemsForCategory = category ? category.items : [];
    const updatedItems = [...items];
    updatedItems[index].categoryName = selectedCategory;
    updatedItems[index].itemName = "";
    updatedItems[index].itemCode = "";
    updatedItems[index].itemImage = "";
    updatedItems[index].pcsPerCtn = "";
    setItems(updatedItems);
    const updatedCategoryItems = [...selectedCategoryItems];
    updatedCategoryItems[index] = itemsForCategory;
    setSelectedCategoryItems(updatedCategoryItems);
  };

  // const handleItemNameChange = (index, event) => {
  //   const selectedItemName = event.target.value;
  //   const selectedItem = allItems.find(
  //     (item) => item.itemName === selectedItemName
  //   );
  //   const updatedItems = [...items];
  //   updatedItems[index].itemName = selectedItemName;
  //   updatedItems[index].itemCode = selectedItem ? selectedItem.itemCode : "";
  //   updatedItems[index].itemImage = selectedItem ? selectedItem.itemImage : "";
  //   updatedItems[index].pcsPerCtn = selectedItem ? selectedItem.pcsPerCtn : "";
  //   updatedItems[index].categoryName = selectedItem
  //     ? getCategoryNameByItem(selectedItem)
  //     : "";
  //   setItems(updatedItems);
  // };

  const getCategoryNameByItem = (item) => {
    for (const category of categories) {
      if (
        category.items.some((catItem) => catItem.itemCode === item.itemCode)
      ) {
        return category.categoryName;
      }
    }
    return "";
  };

  const handleItemNameChange = (index, selectedOption) => {
    const selectedItemName = selectedOption ? selectedOption.value : "";
    const selectedItem = allItems.find(
      (item) => item.itemName === selectedItemName
    );
    const updatedItems = [...items];
    updatedItems[index].itemName = selectedItemName;
    updatedItems[index].itemCode = selectedItem ? selectedItem.itemCode : "";
    updatedItems[index].itemImage = selectedItem ? selectedItem.itemImage : "";
    updatedItems[index].pcsPerCtn = selectedItem ? selectedItem.pcsPerCtn : "";
    updatedItems[index].categoryName = selectedItem
      ? getCategoryNameByItem(selectedItem)
      : "";
    setItems(updatedItems);
  };

  const handleWarehouseChange = (
    index,
    warehouseId,
    inwardReference,
    inwardShipmentMark
  ) => {
    const updatedItems = [...items];
    updatedItems[index].wareHouseName = warehouseId;
    updatedItems[index].inwardReference = inwardReference;
    updatedItems[index].inwardShipmentMark = inwardShipmentMark;
    setItems(updatedItems);
  };

  const calculateTotalBeforeGST = () =>
    items.reduce((total, item) => total + item.qty * item.price, 0).toFixed(2);
  const calculateGSTAmount = () =>
    ((calculateTotalBeforeGST() * parseFloat(gst)) / 100).toFixed(2);
  const calculateTotalWithGST = () =>
    (
      parseFloat(calculateTotalBeforeGST()) + parseFloat(calculateGSTAmount())
    ).toFixed(2);

  const handleCloseModal = () => {
    reset({ gst: 0, clientId: "" });
    setItems([
      {
        categoryName: "",
        itemName: "",
        qty: 1,
        price: 0,
        wareHouseName: "",
        inwardReference: "",
        inwardShipmentMark: "",
      },
    ]);
    setSelectedClient(null); // Reset selected client
    closeModal();
  };

  const getAvailableWarehouses = (item) => {
    return warehouses.reduce((availableWarehouses, warehouse) => {
      const warehouseItems = warehouse.items.filter(
        (warehouseItem) => warehouseItem.itemCode === item.itemCode
      );
      warehouseItems.forEach((warehouseItem) => {
        availableWarehouses.push({
          warehouseId: warehouse._id,
          inwardReference: warehouseItem.inwardReference,
          inwardShipmentMark: warehouseItem.inwardShipmentMark,
          quantity: warehouseItem.quantity,
        });
      });
      return availableWarehouses;
    }, []);
  };

  const handleClientChange = (selectedOption) => {
    const clientId = selectedOption ? selectedOption.value : "";
    const selected = clients.find((client) => client._id === clientId);
    setSelectedClient(selected);
    setValue("clientId", clientId); // Update form value
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    isOpen && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 px-2 sm:px-0">
        <div
          className="bg-white p-4 rounded-md shadow-lg w-full max-w-[90vw] overflow-y-auto"
          style={{ maxHeight: "90vh" }}
        >
          <div
            ref={topRef}
            className="flex items-center justify-between gap-2 mb-4"
          >
            <h2 className="text-2xl font-semibold ">Create Order</h2>

            <button
              className="text-gray-600 hover:text-gray-700 cursor-pointer"
              onClick={closeModal}
            >
              <HiOutlineX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Client and Transporter Section */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
              <div className="w-full text-sm">
                <label className="text-xs font-medium text-gray-700">
                  Client <span className="text-red-600 font-semibold">*</span>
                </label>
                <Controller
                  name="clientId"
                  control={control}
                  rules={{ required: "Client is required" }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      value={
                        selectedClient
                          ? {
                              value: selectedClient._id,
                              label: `${capitalizeWords(
                                selectedClient.firmName
                              )} - ${capitalizeWords(
                                selectedClient.contactPerson
                              )}`,
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(
                          selectedOption ? selectedOption.value : ""
                        );
                        handleClientChange(selectedOption);
                      }}
                      options={clients.map((client) => ({
                        value: client._id,
                        label: `${capitalizeWords(
                          client.firmName
                        )} - ${capitalizeWords(client.contactPerson)}`,
                      }))}
                      styles={style}
                      menuPortalTarget={document.body}
                      placeholder="Select Client"
                      className="w-full"
                    />
                  )}
                />
                {errors.clientId && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.clientId.message}
                  </p>
                )}
              </div>
              {selectedClient && (
                <>
                  <div className="w-full text-sm">
                    <label className="text-xs font-medium text-gray-700">
                      Firm Name
                    </label>
                    <input
                      type="text"
                      value={selectedClient.firmName}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="text-xs font-medium text-gray-700">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={selectedClient.contactPerson}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="text-xs font-medium text-gray-700">
                      Mobile
                    </label>
                    <input
                      type="text"
                      value={selectedClient.mobileNo}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="text-xs font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="text"
                      value={selectedClient.email}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                </>
              )}
              <div className="w-full text-sm">
                <label className="text-xs font-medium text-gray-700">
                  Transporter Name
                </label>
                <Select
                  value={
                    transporterName
                      ? {
                          value: transporterName,
                          label: capitalizeWords(transporterName),
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setTransporterName(
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  options={transporters.map((transporter) => ({
                    value: transporter.transporterName,
                    label: capitalizeWords(transporter.transporterName),
                  }))}
                  isClearable
                  styles={style}
                  menuPortalTarget={document.body}
                  placeholder="Select Transporter"
                  className="w-full"
                />
              </div>
              {/* <div className="w-full text-sm">
                  <label>GST (%)</label>
                  <input
                    type="number"
                    {...register("gst")}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                </div> */}
            </div>

            <div className="hidden sm:grid grid-cols-[repeat(8,1fr)_20px] gap-2 mb-2 bg-gray-300 py-1 rounded-md font-semibold text-xs">
              <span className="pl-2">
                Item Name <span className="text-red-600">*</span>
              </span>
              <span>Category </span>
              <span>Image</span>
              <span>Pcs/Ctn</span>
              <span>
                Quantity <span className="text-red-600">*</span>
              </span>
              <span>Total Carton</span>
              <span>
                Price <span className="text-red-600">*</span>
              </span>
              <span>
                Warehouse <span className="text-red-600">*</span>
              </span>
              <span></span>
            </div>

            {/* Items Section */}
            {items.map((item, index) => (
              <fieldset
                key={index}
                className="border sm:border-0 border-gray-500 p-4 sm:p-0 mb-4 sm:mb-2 rounded-md"
              >
                <legend className="px-2 block sm:hidden font-semibold">
                  Item {index + 1}
                </legend>

                <div className="grid grid-cols-1 md:grid-cols-[repeat(8,1fr)_20px] gap-2">
                  <div className="w-full text-sm">
                    <label className="sm:hidden">
                      Item Name <span className="text-red-600">*</span>
                    </label>
                    <Select
                      name="itemName"
                      value={
                        allItems.find(
                          (option) => option.itemName === item.itemName
                        )
                          ? {
                              value: item.itemName,
                              label: capitalizeWords(item.itemName),
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        handleItemNameChange(index, selectedOption)
                      }
                      options={allItems.map((itemOption) => ({
                        value: itemOption.itemName,
                        label: capitalizeWords(itemOption.itemName),
                      }))}
                      styles={style}
                      menuPortalTarget={document.body}
                      placeholder={`${allItems.length} Items`}
                      className="w-full"
                      required
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">Category</label>
                    <input
                      type="text"
                      name="categoryName"
                      value={item.categoryName}
                      onChange={(e) => handleCategoryChange(index, e)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">Item Image</label>
                    {item.itemImage ? (
                      item.itemImage.includes("drive.google.com") ? (
                        <img
                          src={`https://drive.google.com/thumbnail?id=${
                            item.itemImage.split("id=")[1]
                          }`}
                          alt={item.itemName}
                          className="size-5 rounded-md cursor-pointer"
                          onClick={() => handleImageClick(item.itemImage)}
                        />
                      ) : (
                        <img
                          src={item.itemImage}
                          alt={item.itemName}
                          className="size-5 rounded-md cursor-pointer"
                          onClick={() => handleImageClick(item.itemImage)}
                        />
                      )
                    ) : (
                      <p className="text-xs text-gray-500">No Image</p>
                    )}
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">Pcs/Ctn</label>
                    <input
                      type="text"
                      name="pcsPerCtn"
                      value={item.pcsPerCtn}
                      onChange={(e) => handlePcsPerCtnChange(index, e)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                      disabled
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">
                      Quantity <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      name="qty"
                      value={item.qty}
                      onChange={(e) => handleItemChange(index, e)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">Total Carton</label>
                    <input
                      type="number"
                      name="quantitycarton"
                      value={item.quantitycarton}
                      onChange={(e) => handleItemChange(index, e)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">
                      Price <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, e)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div className="w-full text-sm">
                    <label className="sm:hidden">
                      Warehouse <span className="text-red-600">*</span>
                    </label>
                    <select
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      onChange={(e) =>
                        handleWarehouseChange(
                          index,
                          e.target.value,
                          e.target.selectedOptions[0].getAttribute(
                            "data-inward-reference"
                          ),
                          e.target.selectedOptions[0].getAttribute(
                            "data-inward-shipment-mark"
                          )
                        )
                      }
                      required
                    >
                      <option value="">Select Warehouse</option>
                      {getAvailableWarehouses(item).map((warehouse, idx) => {
                        const isOutOfStock = warehouse.quantity < item.qty;
                        return (
                          <option
                            key={idx}
                            value={warehouse.warehouseId}
                            data-inward-reference={warehouse.inwardReference}
                            data-inward-shipment-mark={
                              warehouse.inwardShipmentMark
                            }
                            disabled={isOutOfStock}
                          >
                            {warehouse.warehouseId} - {warehouse.quantity} -{" "}
                            {warehouse.inwardReference}
                            {isOutOfStock ? " - Out of Stock" : " - Available"}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {items.length > 1 && (
                    <div className="flex bg-red-600 py-1 sm:py-0 rounded-md sm:bg-white items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="sm:text-red-500 flex items-center gap-2 sm:hover:text-red-700 cursor-pointer text-white"
                      >
                        <FiTrash2 size={16} />
                        <span className="block sm:hidden">Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </fieldset>
            ))}

            {/* Total Section */}
            <div className="mt-4 text-right">
              <p className="text-lg font-semibold">
                Total Amount:{" "}
                <span className="text-red-600">{calculateTotalWithGST()}</span>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={addItem}
                className="px-3 py-2 text-xs font-medium flex items-center gap-1 cursor-pointer bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                <FiPlus size={16} />
                Add Item
              </button>
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-3 py-2 text-xs font-medium cursor-pointer bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Close
              </button>
              <button
                type="submit"
                ref={formRef}
                disabled={loading}
                className={`px-3 py-2 text-xs font-medium cursor-pointer bg-red-600 text-white rounded-md hover:bg-red-700 ${
                  loading ? "opacity-75 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Processing..." : "Save"}
              </button>
            </div>
          </form>

          {/* Scroll Buttons */}
          <div className="hidden sm:block">
            {showScrollDown && (
              <button
                onClick={() =>
                  formRef.current?.scrollIntoView({ behavior: "smooth" })
                }
                className="fixed bottom-4 right-4 z-50 cursor-pointer bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md shadow-lg"
              >
                <FaArrowDown />
              </button>
            )}
            {showScrollUp && (
              <button
                onClick={() =>
                  topRef.current?.scrollIntoView({ behavior: "smooth" })
                }
                className="fixed bottom-4 right-16 z-50 cursor-pointer bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-lg"
              >
                <FaArrowUp />
              </button>
            )}
          </div>

          {isImageModalOpen && (
            <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
          )}
          <ToastContainer />
        </div>
      </div>
    )
  );
};

export default ForClientOrderModal;
